// Indian number formatting utilities

import { formatCurrency } from "@/utils/locale";

/**
 * Format a number using Indian number system with commas
 * Examples: 1,000 | 10,000 | 1,00,000 | 10,00,000 | 1,00,00,000
 */
export function formatIndianNumber(value: number | string, decimals: number = 0): string {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  
  if (isNaN(num)) return '';
  
  // Handle negative numbers
  const isNegative = num < 0;
  const absNum = Math.abs(num);
  
  // Round to specified decimal places
  const rounded = decimals > 0 ? absNum.toFixed(decimals) : Math.round(absNum).toString();
  
  // Split into integer and decimal parts
  const [integerPart, decimalPart] = rounded.split('.');
  
  // Format the integer part with Indian comma system
  const formattedInteger = formatIntegerIndian(integerPart);
  
  // Combine with decimal part if present
  const result = decimalPart ? `${formattedInteger}.${decimalPart}` : formattedInteger;
  
  return isNegative ? `-${result}` : result;
}

/**
 * Format integer part with Indian comma system
 * Last 3 digits, then groups of 2
 */
function formatIntegerIndian(integerStr: string): string {
  if (integerStr.length <= 3) return integerStr;
  
  // Get last 3 digits
  const lastThree = integerStr.slice(-3);
  const remaining = integerStr.slice(0, -3);
  
  // Split remaining into groups of 2 from right
  const groups: string[] = [];
  let temp = remaining;
  while (temp.length > 0) {
    groups.unshift(temp.slice(-2));
    temp = temp.slice(0, -2);
  }
  
  return [...groups, lastThree].join(',');
}

/**
 * Format currency with Indian number system (₹ prefix)
 */
export function formatIndianCurrency(value: number, decimals: number = 0): string {
  return `₹${formatIndianNumber(value, decimals)}`;
}

/**
 * Format currency with short notation (L, Cr) for display
 */
export function formatCurrencyShort(value: number): string {
  if (value >= 10000000) {
    return `₹${(value / 10000000).toFixed(2)} Cr`;
  } else if (value >= 100000) {
    return `₹${(value / 100000).toFixed(2)} L`;
  } else if (value >= 1000) {
    return `₹${(value / 1000).toFixed(2)} K`;
  }
  return `₹${value.toFixed(0)}`;
}

/**
 * Parse a formatted Indian number string back to a number
 * Removes commas and handles various formats
 */
export function parseIndianNumber(value: string): number {
  if (!value || value === '') return 0;
  
  // Remove commas, spaces, and currency symbols
  const cleaned = value.replace(/[,\s₹]/g, '');
  
  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? 0 : parsed;
}

/**
 * Get the raw numeric value from a formatted string for input handling
 */
export function getRawValue(formattedValue: string): string {
  return formattedValue.replace(/[,\s₹]/g, '');
}

/**
 * Format input value while typing - handles cursor position
 * Removes leading zeros and formats with Indian commas
 */
export function formatInputValue(value: string): string {
  // Remove all non-numeric characters except decimal point and minus
  let cleaned = value.replace(/[^0-9.-]/g, '');
  
  // Handle empty or just minus sign
  if (cleaned === '' || cleaned === '-') return cleaned;
  
  // Remove leading zeros (but keep "0." for decimals)
  if (cleaned.length > 1 && cleaned.startsWith('0') && cleaned[1] !== '.') {
    cleaned = cleaned.replace(/^0+/, '') || '0';
  }
  
  // Handle negative with leading zeros
  if (cleaned.startsWith('-0') && cleaned.length > 2 && cleaned[2] !== '.') {
    cleaned = '-' + cleaned.slice(1).replace(/^0+/, '');
  }
  
  // Split by decimal point
  // const parts = cleaned.split('.');
  // const integerPart = parts[0] || '';
  // const decimalPart = parts[1];
  
  // // Format integer part
  // const isNegative = integerPart.startsWith('-');
  // const absInteger = isNegative ? integerPart.slice(1) : integerPart;
  // const formattedInteger = absInteger ? formatIntegerIndian(absInteger) : '';
  
  // // Combine parts
  // let result = isNegative ? `-${formattedInteger}` : formattedInteger;
  
  // if (decimalPart !== undefined) {
  //   result += '.' + decimalPart;
  // }
  
  const result =formatCurrency(Number(cleaned))
  return result;
}

/**
 * Calculate new cursor position after formatting
 */
export function calculateCursorPosition(
  oldValue: string,
  newValue: string,
  oldCursor: number
): number {
  // Count commas before cursor in old value
  const oldCommasBefore = (oldValue.slice(0, oldCursor).match(/,/g) || []).length;
  
  // Count non-comma characters before cursor in old value
  const oldCharsBefore = oldCursor - oldCommasBefore;
  
  // Find position in new value that has same number of non-comma chars
  let newCursor = 0;
  let charCount = 0;
  
  for (let i = 0; i < newValue.length && charCount < oldCharsBefore; i++) {
    if (newValue[i] !== ',') {
      charCount++;
    }
    newCursor = i + 1;
  }
  
  return newCursor;
}
